// Unified tab / accordion controller.
// Container reads behaviour from data attributes:
//   data-display-lg / data-display-md / data-display-xs  → "tab" | "accordion"
//   data-exclusive    → "true" | "false"  (accordion only: one panel at a time)
//   data-equal-height → "true" | "false"  (tab mode: all panels same min-height)
//
// PHP marks the initial open panel with is-active / no hidden attr.
// JS normalises on init and takes over after that.

import { getBreakpoints, getCurrentBreakpoint, resetBreakpointCache } from '../core/utils/breakpoints.js';

let activePanels = new Set();
let resizeListenerAdded = false;

function getMode(group) {
    const bp = getCurrentBreakpoint();
    const key = 'display' + bp[0].toUpperCase() + bp.slice(1);
    return group.dataset[key] || 'tab';
}

export function initializeTabs(container = document) {
    const tabGroups = container.querySelectorAll('.js-tabs');
    if (!tabGroups.length) return;

    if (!resizeListenerAdded) {
        window.addEventListener('resize', onResize);
        window.addEventListener('orientationchange', onResize);
        resizeListenerAdded = true;
    }

    tabGroups.forEach(group => {
        if (group.classList.contains('is-initialized')) return;
        group.classList.add('is-initialized');

        const controller = new AbortController();
        group._tabsAbortController = controller;
        const { signal } = controller;

        const navBtns = group.querySelectorAll(':scope .js-tabs-nav-btn');
        const accBtns = group.querySelectorAll(':scope .js-tabs-acc-btn');
        const panels  = group.querySelectorAll(':scope .js-tabs-panel');

        if (!panels.length) return;

        const exclusive   = group.dataset.exclusive   === 'true';
        const equalHeight = group.dataset.equalHeight === 'true';

        // ─── Accordion helpers ────────────────────────────────────────────────

        function openPanelAcc(panel) {
            if (!panel) return;
            if (panel._transitionTimeout) {
                clearTimeout(panel._transitionTimeout);
                panel._transitionTimeout = null;
            }
            panel.removeAttribute('hidden');
            panel.querySelectorAll('.layout').forEach(l => l.classList.add('is-inview'));
            if (!panel.classList.contains('is-active')) panel.style.height = '0px';
            panel.offsetHeight; // force reflow
            panel.style.height = panel.scrollHeight + 'px';
            panel.classList.add('is-active');
            activePanels.add(panel);
        }

        function closePanelAcc(panel) {
            if (!panel) return;
            panel.style.height = panel.offsetHeight + 'px';
            panel.offsetHeight; // force reflow
            panel.style.height = '0px';
            panel.classList.remove('is-active');
            activePanels.delete(panel);
            if (panel._transitionTimeout) clearTimeout(panel._transitionTimeout);
            panel._transitionTimeout = setTimeout(() => {
                if (!panel.classList.contains('is-active')) panel.setAttribute('hidden', '');
                panel._transitionTimeout = null;
            }, 500);
        }

        // ─── Tab (desktop overlay) helpers ───────────────────────────────────

        function switchTabDesktop(targetId) {
            const oldPanel = Array.from(panels).find(p => p.classList.contains('is-active'));
            const newPanel = Array.from(panels).find(p => p.id === targetId);
            if (!newPanel || newPanel === oldPanel) return;

            const body = panels[0].parentElement;
            if (!body) return;

            ['_timeoutFadeOut', '_timeoutHeight', '_timeoutFadeIn'].forEach(k => {
                if (body[k]) { clearTimeout(body[k]); body[k] = null; }
            });

            panels.forEach(p => {
                if (p !== oldPanel && p !== newPanel) {
                    p.classList.remove('is-active');
                    p.setAttribute('hidden', '');
                    p.style.height = '0px';
                }
            });

            if (equalHeight) {
                // Cross-fade only — no height animation needed.
                if (oldPanel) oldPanel.classList.remove('is-active');
                body._timeoutFadeOut = setTimeout(() => {
                    if (oldPanel) { oldPanel.setAttribute('hidden', ''); oldPanel.style.height = '0px'; }
                    newPanel.removeAttribute('hidden');
                    newPanel.querySelectorAll('.layout').forEach(l => l.classList.add('is-inview'));
                    newPanel.style.height = '';
                    body._timeoutFadeOut = setTimeout(() => {
                        newPanel.classList.add('is-active');
                        body._timeoutFadeOut = null;
                    }, 50);
                }, 500);
            } else {
                // Fade out → resize body → fade in.
                const oldHeight = body.offsetHeight;
                body.style.height = oldHeight + 'px';
                if (oldPanel) oldPanel.classList.remove('is-active');

                body._timeoutFadeOut = setTimeout(() => {
                    if (oldPanel) { oldPanel.setAttribute('hidden', ''); oldPanel.style.height = '0px'; }
                    newPanel.removeAttribute('hidden');
                    newPanel.querySelectorAll('.layout').forEach(l => l.classList.add('is-inview'));
                    newPanel.style.height = '';
                    body.style.height = newPanel.scrollHeight + 'px';

                    body._timeoutHeight = setTimeout(() => {
                        newPanel.classList.add('is-active');
                        body._timeoutFadeIn = setTimeout(() => {
                            body.style.height = '';
                            body._timeoutFadeOut = null;
                            body._timeoutHeight  = null;
                            body._timeoutFadeIn  = null;
                        }, 500);
                    }, 500);
                }, 500);
            }
        }

        // ─── Button sync ──────────────────────────────────────────────────────

        function syncButtons(activeIds) {
            navBtns.forEach(btn => {
                const on = activeIds.has(btn.dataset.tabTarget);
                btn.classList.toggle('is-active', on);
                btn.setAttribute('aria-selected', on ? 'true' : 'false');
            });
            accBtns.forEach(btn => {
                const on = activeIds.has(btn.dataset.tabTarget);
                btn.classList.toggle('is-active', on);
                btn.setAttribute('aria-expanded', on ? 'true' : 'false');
            });
        }

        // ─── Equal-height measurement ─────────────────────────────────────────

        function measureEqualHeight() {
            if (!equalHeight) return;
            const body = panels[0]?.parentElement;
            if (!body) return;
            let maxH = 0;
            panels.forEach(panel => {
                panel.classList.add('is-measuring');
                maxH = Math.max(maxH, panel.scrollHeight);
                panel.classList.remove('is-measuring');
            });
            body.style.minHeight = maxH + 'px';
        }

        // ─── Apply mode (init + resize mode-change) ───────────────────────────

        function applyMode(mode, activeIds) {
            group.classList.toggle('is-tab-mode',       mode !== 'accordion');
            group.classList.toggle('is-accordion-mode', mode === 'accordion');

            if (mode === 'accordion') {
                const body = panels[0]?.parentElement;
                if (body) { body.style.height = ''; body.style.minHeight = ''; }

                panels.forEach(panel => {
                    if (activeIds.has(panel.id)) {
                        panel.classList.add('is-active');
                        panel.removeAttribute('hidden');
                        panel.style.height = panel.scrollHeight + 'px';
                        activePanels.add(panel);
                    } else {
                        panel.classList.remove('is-active');
                        panel.setAttribute('hidden', '');
                        panel.style.height = '0px';
                        activePanels.delete(panel);
                    }
                });
            } else {
                // Tab mode: collapse to one active panel.
                const firstId = activeIds.size > 0 ? activeIds.values().next().value : panels[0]?.id;
                const singleIds = firstId ? new Set([firstId]) : new Set();
                activeIds = singleIds;

                const body = panels[0]?.parentElement;
                if (body) { body.style.height = ''; }

                panels.forEach(panel => {
                    if (panel.id === firstId) {
                        panel.classList.add('is-active');
                        panel.removeAttribute('hidden');
                        panel.style.height = '';
                        activePanels.add(panel);
                    } else {
                        panel.classList.remove('is-active');
                        panel.setAttribute('hidden', '');
                        panel.style.height = '0px';
                        activePanels.delete(panel);
                    }
                });

                measureEqualHeight();
            }

            syncButtons(activeIds);
            group._tabsActiveIds = activeIds;
        }

        // ─── Unified switch (called on click) ────────────────────────────────

        function switchTab(targetId, updateHash = false) {
            const mode = getMode(group);

            if (mode === 'accordion') {
                if (exclusive) {
                    panels.forEach(p => {
                        if (p.id === targetId)                         openPanelAcc(p);
                        else if (p.classList.contains('is-active'))    closePanelAcc(p);
                        else { p.classList.remove('is-active'); p.setAttribute('hidden', ''); p.style.height = '0px'; activePanels.delete(p); }
                    });
                    group._tabsActiveIds = new Set([targetId]);
                } else {
                    const panel = Array.from(panels).find(p => p.id === targetId);
                    if (panel) openPanelAcc(panel);
                    group._tabsActiveIds.add(targetId);
                }
                syncButtons(group._tabsActiveIds);
            } else {
                switchTabDesktop(targetId);
                group._tabsActiveIds = new Set([targetId]);
                syncButtons(group._tabsActiveIds);
            }

            if (updateHash && targetId) history.replaceState(null, null, '#' + targetId);
        }

        // ─── Determine initial active panels ─────────────────────────────────

        let initialActiveIds = new Set();

        if (window.location.hash) {
            const hashId = window.location.hash.substring(1);
            if (Array.from(panels).some(p => p.id === hashId)) {
                initialActiveIds.add(hashId);
            }
        }

        if (!initialActiveIds.size) {
            const initMode = getMode(group);
            if (initMode === 'accordion' && !exclusive) {
                // Non-exclusive accordion: honour all server-marked panels; default none open.
                panels.forEach(p => { if (p.classList.contains('is-active')) initialActiveIds.add(p.id); });
            } else {
                // Tab or exclusive accordion: single initial panel.
                const marked = Array.from(panels).find(p => p.classList.contains('is-active') || !p.hasAttribute('hidden'));
                if (marked) {
                    initialActiveIds.add(marked.id);
                } else if (initMode !== 'accordion' && panels[0]) {
                    // Tab mode always has something open.
                    initialActiveIds.add(panels[0].id);
                }
                // Exclusive accordion: leave all closed if none marked.
            }
        }

        group._tabsActiveIds  = initialActiveIds;
        group._tabsCurrentMode = getMode(group);
        group._tabsReinit = newMode => applyMode(newMode, group._tabsActiveIds);

        applyMode(group._tabsCurrentMode, initialActiveIds);

        // ─── Event listeners ──────────────────────────────────────────────────

        navBtns.forEach(btn => {
            btn.addEventListener('click', e => {
                e.preventDefault();
                switchTab(btn.dataset.tabTarget, true);
            }, { signal });
        });

        accBtns.forEach(btn => {
            btn.addEventListener('click', e => {
                e.preventDefault();
                const targetId = btn.dataset.tabTarget;
                const mode     = getMode(group);
                const isActive = btn.classList.contains('is-active');

                // In accordion mode, clicking an open panel toggles it closed.
                if (mode === 'accordion' && isActive) {
                    btn.classList.remove('is-active');
                    btn.setAttribute('aria-expanded', 'false');
                    navBtns.forEach(n => {
                        if (n.dataset.tabTarget === targetId) {
                            n.classList.remove('is-active');
                            n.setAttribute('aria-selected', 'false');
                        }
                    });
                    const panel = Array.from(panels).find(p => p.id === targetId);
                    if (panel) closePanelAcc(panel);
                    group._tabsActiveIds.delete(targetId);
                } else {
                    switchTab(targetId, true);
                }
            }, { signal });
        });
    });
}

export function destroyTabs() {
    resetBreakpointCache();
    const tabGroups = document.querySelectorAll('.js-tabs.is-initialized');
    tabGroups.forEach(group => {
        const body = group.querySelector('.js-tabs-panel')?.parentElement;
        if (body) {
            ['_timeoutFadeOut', '_timeoutHeight', '_timeoutFadeIn'].forEach(k => {
                if (body[k]) { clearTimeout(body[k]); body[k] = null; }
            });
        }
        group.classList.remove('is-initialized', 'is-tab-mode', 'is-accordion-mode');
        if (group._tabsAbortController) {
            group._tabsAbortController.abort();
            delete group._tabsAbortController;
        }
        delete group._tabsActiveIds;
        delete group._tabsCurrentMode;
        delete group._tabsReinit;
    });
    window.removeEventListener('resize', onResize);
    window.removeEventListener('orientationchange', onResize);
    resizeListenerAdded = false;
    activePanels.clear();
}

function onResize() {
    resetBreakpointCache();

    document.querySelectorAll('.js-tabs.is-initialized').forEach(group => {
        const newMode  = getMode(group);
        const prevMode = group._tabsCurrentMode;

        if (newMode !== prevMode) {
            group._tabsCurrentMode = newMode;
            // Collapsing multiple active panels to one when entering tab mode.
            if (newMode !== 'accordion' && group._tabsActiveIds?.size > 1) {
                group._tabsActiveIds = new Set([group._tabsActiveIds.values().next().value]);
            }
            if (group._tabsReinit) group._tabsReinit(newMode);
        } else if (newMode === 'accordion') {
            // Recalculate open panel heights after resize.
            activePanels.forEach(panel => {
                if (panel.closest('.js-tabs') !== group) return;
                panel.style.height = 'auto';
                const h = panel.scrollHeight;
                requestAnimationFrame(() => { panel.style.height = h + 'px'; });
            });
        }
        // Tab mode + same breakpoint: equal-height handled by CSS transition on body.
    });
}
